import { startCase } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';
import { ReactNode, useCallback, useMemo, useRef } from 'react';

import { JobPosition } from '@pflegia/data-access';
import { BasePositionCardProps } from '@pflegia/types/common/interfaces/position-card';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import useIntersectionObserver, { UseIntersectionObserverOptions } from '@pflegia/ui/hooks/use-intersection-observer';
import { useJobListSortStore } from '@pflegia/ui/hooks/use-sort';

import { TrackingEventName } from '@shared/application/marketing/events/tracking-event-name';
import { useTrackJobPositionFeatures } from '@shared/hooks/use-tracking-job-position-features';

export interface JobPositionCardTrackerProps extends Pick<BasePositionCardProps, 'featuresBadges'> {
  jobPosition: Pick<JobPosition, 'id' | 'jobTitles'>;
  isCompleted?: boolean;
  children: ReactNode;
  slimmedJobCard?: boolean;
}

export const JobPositionCardTracker = ({
  jobPosition,
  featuresBadges,
  children,
  isCompleted,
  slimmedJobCard = false,
}: JobPositionCardTrackerProps) => {
  const authenticated = useAuth();
  const cardRef = useRef<HTMLDivElement>(null);
  const { handleTracking } = useTrackJobPositionFeatures();
  const { sort: sortedBy } = useJobListSortStore.getState();
  const trackingProps = useMemo(
    // eslint-disable-next-line eslint-compat/camelcase
    () => ({ jobPosition, featuresBadges, authenticated, slimmedJobCard, sorted_by: startCase(sortedBy) }),
    [jobPosition, featuresBadges, authenticated, slimmedJobCard, sortedBy],
  );
  const { t } = useTranslation();

  const handleIntersection = useCallback<UseIntersectionObserverOptions['callback']>(
    entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0) {
          if (isCompleted) handleTracking(TrackingEventName.JobPositionCardViewed, trackingProps);
        }
      });
    },
    [handleTracking, isCompleted, trackingProps],
  );

  const handleClickEvent = useCallback(
    (event: React.MouseEvent) => {
      if (!isCompleted) return;
      const target = event.target as HTMLElement;
      const targetedElement = ['button', 'a'].map(selector => target.closest(selector));

      if (targetedElement.every(elt => elt?.textContent !== t('job:positionCard.buttons.apply'))) {
        handleTracking(TrackingEventName.JobPositionCardClicked, trackingProps);
      }
    },
    [handleTracking, isCompleted, t, trackingProps],
  );

  useIntersectionObserver({
    callback: handleIntersection,
    ref: cardRef,
  });

  return (
    <div ref={cardRef} onClick={handleClickEvent}>
      {children}
    </div>
  );
};
